import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import Checkbox from 'apollo-react/components/Checkbox';
import Table from 'apollo-react/components/Table';
import IconButton from 'apollo-react/components/IconButton';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import OpenNew from 'apollo-react-icons/OpenNew';
import FiltersPanel from '../Reviews_Cards/FiltersPanel';
import { parseDateRange, checkDateInRange } from '../../utils/dateUtils';
import styles from './LinkedStudies.module.css';

const TEXT_DEBOUNCE_MS = 500;

/**
 * LinkedStudiesSelector - Component for selecting linked studies
 *
 * Displays a list of eligible studies (approved + ended) that can be linked
 * to a new request. Used when workflow has linked_studies_enabled configuration.
 *
 * @param {Object} props
 * @param {Array} props.eligibleStudies - List of eligible study requests
 * @param {Array} props.selectedStudyIds - Array of selected study IDs
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
const LinkedStudiesSelector = ({
  eligibleStudies = [],
  selectedStudyIds = [],
  onSelectionChange,
  loading = false,
  error = null,
}) => {
  const { t } = useTranslation();
  const textDebounceRef = useRef();

  const [filters, setFilters] = useState({
    referenceOrStudyName: '',
    dateRange: null,
  });
  const [debouncedTextFilter, setDebouncedTextFilter] = useState('');

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    clearTimeout(textDebounceRef.current);
    textDebounceRef.current = setTimeout(() => {
      setDebouncedTextFilter(filters.referenceOrStudyName);
    }, TEXT_DEBOUNCE_MS);

    return () => clearTimeout(textDebounceRef.current);
  }, [filters.referenceOrStudyName]);

  const filteredStudies = useMemo(() => {
    if (!eligibleStudies?.length) return [];

    const searchTerm = debouncedTextFilter.toLowerCase();
    let startDate = null;
    let endDate = null;
    if (filters.dateRange) {
      ({ startDate, endDate } = parseDateRange(filters.dateRange));
    }
    const hasDateFilter = !!startDate || !!endDate;

    return eligibleStudies.filter((study) => {
      const textMatch =
        !searchTerm ||
        study.study_name?.toLowerCase().includes(searchTerm) ||
        study.generated_id?.toLowerCase().includes(searchTerm);

      if (!textMatch) return false;
      if (!hasDateFilter) return true;

      const dateEnded = study.date_ended ? new Date(study.date_ended) : null;
      return dateEnded
        ? checkDateInRange(dateEnded, startDate, endDate)
        : false;
    });
  }, [eligibleStudies, debouncedTextFilter, filters.dateRange]);

  const handleToggleStudy = useCallback(
    (studyId) => {
      const newSelection = selectedStudyIds.includes(studyId)
        ? selectedStudyIds.filter((id) => id !== studyId)
        : [...selectedStudyIds, studyId];
      onSelectionChange(newSelection);
    },
    [selectedStudyIds, onSelectionChange]
  );

  const handleSelectAll = useCallback(() => {
    const filteredIds = filteredStudies.map((s) => s.id);
    const allFilteredSelected =
      filteredIds.length > 0 &&
      filteredIds.every((id) => selectedStudyIds.includes(id));

    if (allFilteredSelected) {
      onSelectionChange(
        selectedStudyIds.filter((id) => !filteredIds.includes(id))
      );
    } else {
      const newSelection = [...new Set([...selectedStudyIds, ...filteredIds])];
      onSelectionChange(newSelection);
    }
  }, [filteredStudies, selectedStudyIds, onSelectionChange]);

  const columns = useMemo(
    () => [
      {
        header: (
          <Checkbox
            checked={
              filteredStudies.length > 0 &&
              filteredStudies.every((s) => selectedStudyIds.includes(s.id))
            }
            indeterminate={
              filteredStudies.some((s) => selectedStudyIds.includes(s.id)) &&
              !filteredStudies.every((s) => selectedStudyIds.includes(s.id))
            }
            onChange={handleSelectAll}
          />
        ),
        accessor: 'select',
        customCell: ({ row }) => (
          <Checkbox
            checked={selectedStudyIds.includes(row.id)}
            onChange={() => handleToggleStudy(row.id)}
          />
        ),
        width: 60,
      },
      {
        header: t('study_name'),
        accessor: 'study_name',
        customCell: ({ row }) => row.study_name || row.generated_id,
      },
      {
        header: t('ID'),
        accessor: 'generated_id',
      },
      {
        header: t('Creation Date'),
        accessor: 'date_ended',
        customCell: ({ row }) =>
          row.date_ended ? new Date(row.date_ended).toLocaleDateString() : '-',
      },
      {
        accessor: 'actions',
        customCell: ({ row }) => {
          const requestName = row.study_name || row.generated_id;
          const handleViewDetails = (e) => {
            e.stopPropagation();
            const url = `/enduser/progress/requests/${row.id}?request_name=${encodeURIComponent(requestName)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          };
          return (
            <IconButton
              size="small"
              onClick={handleViewDetails}
              title={t('view_details')}
            >
              <OpenNew />
            </IconButton>
          );
        },
        width: 80,
      },
    ],
    [t, selectedStudyIds, filteredStudies, handleSelectAll, handleToggleStudy]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {selectedStudyIds.length > 0 && (
        <div className={styles.selectionSummary}>
          <Typography variant="bodySmall" className={styles.selectionCount}>
            {selectedStudyIds.length} {t('selected')}
          </Typography>
        </div>
      )}

      {eligibleStudies.length === 0 ? (
        <div className={styles.emptyContainer}>
          <Typography variant="bodyDefault">
            {t('noEligibleStudies')}
          </Typography>
        </div>
      ) : (
        <div className={styles.filterLayout}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FiltersPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                tagOptions={[]}
                selectedTags={[]}
                onTagChange={() => {}}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              {filteredStudies.length === 0 ? (
                <div className={styles.emptyContainer}>
                  <Typography variant="bodyDefault">
                    {t('noFilterResults')}
                  </Typography>
                </div>
              ) : (
                <Table
                  columns={columns}
                  rows={filteredStudies}
                  rowId="id"
                  hidePagination={filteredStudies.length <= 10}
                />
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </div>
  );
};

export default LinkedStudiesSelector;
